package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class LAvatarSize(val pixels: Int) {
    SM(24),
    MD(32),
    LG(40),
}

val LAvatarStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .color(colorMode.toSitePalette().textSecondary)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Pill)
        .fontWeight(600)
        .lineHeight(1)
        .overflow(Overflow.Hidden)
}

@Composable
fun LAvatar(
    name: String,
    modifier: Modifier = Modifier,
    imageUrl: String? = null,
    size: LAvatarSize = LAvatarSize.MD,
    background: Color? = null,
    foreground: Color? = null,
    // Two keys rather than one with an optional placeholder: "{name} avatar" is a possessive in
    // most languages and "User avatar" is not, so a single template would force one grammar onto
    // both. The named argument is what lets a translation move the name where its language puts it.
    label: String = if (name.isBlank()) {
        t("ds.ds.lavatar.user_avatar", "User avatar")
    } else {
        tf("ds.ds.lavatar.name_avatar", "{name} avatar", "name" to name)
    },
) {
    val palette = ColorMode.current.toSitePalette()
    val dimension = size.pixels.px
    val fontSize = when (size) {
        LAvatarSize.SM -> TypeScale.Micro
        LAvatarSize.MD -> TypeScale.Small
        LAvatarSize.LG -> TypeScale.BodySmall
    }
    Span(
        LAvatarStyle.toModifier()
            .width(dimension)
            .height(dimension)
            .fontSize(fontSize)
            .backgroundColor(background ?: palette.surfaceSunken)
            .color(foreground ?: palette.textSecondary)
            .then(modifier)
            .toAttrs {
                attr("role", "img")
                attr("aria-label", label)
            },
    ) {
        if (imageUrl.isNullOrBlank()) {
            Span(Modifier.toAttrs { attr("aria-hidden", "true") }) {
                Text(name.toAvatarInitials())
            }
        } else {
            Img(
                src = imageUrl,
                alt = "",
                attrs = Modifier
                    .display(DisplayStyle.Block)
                    .width(100.percent)
                    .height(100.percent)
                    .styleModifier { property("object-fit", "cover") }
                    .toAttrs {
                        attr("aria-hidden", "true")
                    },
            )
        }
    }
}

private fun String.toAvatarInitials(): String {
    val words = trim().split(Regex("\\s+")).filter(String::isNotBlank)
    return when (words.size) {
        0 -> "?"
        1 -> words.first().take(2)
        else -> words.first().take(1) + words.last().take(1)
    }.uppercase()
}
